import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import { differenceInHours } from "date-fns";
import { isEmpty } from "helpers";
import { AvailablePartsSearch } from "models/AvailablePartsSearch";
import { StockStore } from "operations/schema/schema";
import { AppAsyncThunkConfig } from "store";
import { State } from "./cache.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncQueries = {
  getCachePrefill: createAppAsyncThunk(
    "cache/getCachePrefill",
    async (props: { force?: boolean }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { lastLoaded } = getState().cache;
      if (!props.force && lastLoaded && differenceInHours(new Date(lastLoaded), new Date()) < 72) {
        return rejectWithValue("No need to refresh yet");
      }
      const { data, errors } = await graphqlRequest(sdk.getCachePrefill);
      if (errors) return rejectWithValue(errors);
      if (!data?.jobActions) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  getPartnerCachePrefill: createAppAsyncThunk(
    "cache/getPartnerCachePrefill",
    async (
      props: { force?: boolean; token?: string },
      { getState, rejectWithValue, extra: { sdk } },
    ) => {
      const { lastLoaded } = getState().cache;
      if (!props.force && lastLoaded && differenceInHours(new Date(lastLoaded), new Date()) < 72) {
        return rejectWithValue("No need to refresh yet");
      }
      const { data, errors } = await graphqlRequest(sdk.getPartnerCachePrefill);
      if (errors) return rejectWithValue(errors);
      if (!data?.jobActions) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  getPlannerCachePrefill: createAppAsyncThunk(
    "cache/getPlannerCachePrefill",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getPlannerCachePrefill);
      if (errors) return rejectWithValue(errors);
      if (!data?.symptoms) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  getAvailableParts: createAppAsyncThunk(
    "cache/getAvailableParts",
    async (props: AvailablePartsSearch, { getState, rejectWithValue, extra: { sdk } }) => {
      const { location, searchString } = props;
      const {
        cache: { technicianPartsLastLoaded, locationalPartsLastLoaded },
      } = getState();
      const hoursToCache = 12;
      if (
        (location.stockStore === StockStore.Engineer &&
          technicianPartsLastLoaded &&
          differenceInHours(new Date(technicianPartsLastLoaded), new Date()) < hoursToCache) ||
        (location.stockStore !== StockStore.Engineer &&
          isEmpty(searchString) &&
          locationalPartsLastLoaded &&
          differenceInHours(new Date(locationalPartsLastLoaded), new Date()) < hoursToCache)
      ) {
        return rejectWithValue("No need to refresh yet");
      }

      const { data, errors } = await graphqlRequest(sdk.getAvailableParts, {
        variables: {
          location,
          searchString: searchString,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.parts) return rejectWithValue("something went wrong");
      return { data, props };
    },
  ),
  getTechnicians: createAppAsyncThunk(
    "cache/getTechnicians",
    async (_, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getTechnicians);
      if (errors) return rejectWithValue(errors);
      if (!data?.technicians) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  getPartsTechnician: createAppAsyncThunk(
    "cache/getPartsTechnician",
    async (props: { force?: boolean }, { getState, rejectWithValue, extra: { sdk } }) => {
      const {
        cache: { technicianPartsLastLoaded },
        user: { userVar },
      } = getState();
      if (
        !props.force &&
        technicianPartsLastLoaded &&
        differenceInHours(new Date(technicianPartsLastLoaded), new Date()) < 12
      ) {
        return rejectWithValue("No need to refresh yet");
      }
      const { data, errors } = await graphqlRequest(sdk.getAvailableParts, {
        variables: {
          location: {
            locationId: userVar?.stockId,
            stockStore: StockStore.Engineer,
          },
          searchString: "",
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.parts) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  getRequestableParts: createAppAsyncThunk(
    "cache/getRequestableParts",
    async (props: { force?: boolean }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { requestablePartsLastLoaded } = getState().cache;
      if (
        !props.force &&
        requestablePartsLastLoaded &&
        differenceInHours(new Date(requestablePartsLastLoaded), new Date()) < 1
      ) {
        return rejectWithValue("No need to refresh yet");
      }
      const { data, errors } = await graphqlRequest(sdk.getRequestableParts);
      if (errors) return rejectWithValue(errors);
      if (!data?.requestableParts) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  getSuggestedParts: createAppAsyncThunk(
    "cache/getSuggestedParts",
    async (variables: { machineId: string }, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getSuggestedParts, {
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.suggestedParts) return rejectWithValue("something went wrong");
      return { variables, data };
    },
  ),
  getFrequencyParts: createAppAsyncThunk(
    "cache/getFrequencyParts",
    async (variables: { modelGuid: string }, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getFrequencyParts, {
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.frequencyParts) return rejectWithValue("something went wrong");
      return { variables, data };
    },
  ),
};

export const queryBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncQueries.getCachePrefill.pending, (state) => {
    state.loading.prefill = true;
    return state;
  });
  builder.addCase(asyncQueries.getCachePrefill.rejected, (state) => {
    state.loading.prefill = false;
    return state;
  });
  builder.addCase(asyncQueries.getCachePrefill.fulfilled, (state, { payload: data }) => {
    state.lastLoaded = new Date().toISOString();
    state.loading.prefill = false;
    state.actions = [...data.jobActions];
    state.extras = [...data.extras];
    state.orderLineInfo = { ...data.orderLineInfo };
    state.partSortGroups = { ...data.partSortGroups };
    state.suppliers = [...data.suppliers];
    state.symptoms = [...data.symptoms];
    state.availabilityTypes = [...data.availabilityTypes];
    state.departments = [...data.departments];
    state.machineProperties = [...data.machineProperties];
    return state;
  });
  builder.addCase(asyncQueries.getPartnerCachePrefill.pending, (state) => {
    state.loading.prefill = true;
    return state;
  });
  builder.addCase(asyncQueries.getPartnerCachePrefill.rejected, (state) => {
    state.loading.prefill = false;
    return state;
  });
  builder.addCase(asyncQueries.getPartnerCachePrefill.fulfilled, (state, { payload: data }) => {
    state.lastLoaded = new Date().toISOString();
    state.loading.prefill = false;
    state.actions = [...data.jobActions];
    state.extras = [...data.extras];
    state.orderLineInfo = { ...data.orderLineInfo };
    state.partSortGroups = { ...data.partSortGroups };
    state.suppliers = [...data.suppliers];
    state.symptoms = [...data.symptoms];
    state.machineProperties = [...data.machineProperties];
    return state;
  });
  builder.addCase(asyncQueries.getPlannerCachePrefill.pending, (state) => {
    state.loading.plannerPrefill = true;
    return state;
  });
  builder.addCase(asyncQueries.getPlannerCachePrefill.rejected, (state) => {
    state.loading.plannerPrefill = false;
    return state;
  });
  builder.addCase(asyncQueries.getPlannerCachePrefill.fulfilled, (state, { payload: data }) => {
    state.lastLoaded = new Date().toISOString();
    state.loading.plannerPrefill = false;
    state.areas = [...data.areas];
    state.plannerNodes = [...data.plannerNodes];
    state.symptoms = [...data.symptoms];
    return state;
  });
  builder.addCase(asyncQueries.getAvailableParts.pending, (state) => {
    state.loading.availableParts = true;
    return state;
  });
  builder.addCase(asyncQueries.getAvailableParts.rejected, (state) => {
    state.loading.availableParts = false;
    return state;
  });
  builder.addCase(
    asyncQueries.getAvailableParts.fulfilled,
    (
      state,
      {
        payload: {
          data,
          props: { location, searchString },
        },
      },
    ) => {
      state.loading.availableParts = false;
      if (location.stockStore === StockStore.Engineer) {
        state.availableParts.technicianParts = [...data.parts];
        state.technicianPartsLastLoaded = new Date().toISOString();
      } else {
        if (searchString && searchString?.length > 0) {
          state.availableParts.liveSearchParts = [...data.parts];
        } else {
          state.availableParts.locationalParts = [...data.parts];
          state.locationalPartsLastLoaded = new Date().toISOString();
        }
      }
      return state;
    },
  );
  builder.addCase(asyncQueries.getTechnicians.pending, (state) => {
    state.loading.technicians = true;
    return state;
  });
  builder.addCase(asyncQueries.getTechnicians.rejected, (state, { payload: errors }) => {
    state.loading.technicians = false;
    return state;
  });
  builder.addCase(asyncQueries.getTechnicians.fulfilled, (state, { payload: { technicians } }) => {
    state.loading.technicians = false;
    state.technicians = [...technicians];
    return state;
  });
  builder.addCase(asyncQueries.getPartsTechnician.pending, (state) => {
    state.loading.technicianParts = true;
    return state;
  });
  builder.addCase(asyncQueries.getPartsTechnician.rejected, (state) => {
    state.loading.technicianParts = false;
    return state;
  });
  builder.addCase(asyncQueries.getPartsTechnician.fulfilled, (state, { payload: data }) => {
    state.loading.technicianParts = false;
    state.availableParts.technicianParts = [...data.parts];
    state.technicianPartsLastLoaded = new Date().toISOString();
    return state;
  });
  builder.addCase(asyncQueries.getRequestableParts.pending, (state) => {
    state.loading.requestableParts = true;
    return state;
  });
  builder.addCase(asyncQueries.getRequestableParts.rejected, (state) => {
    state.loading.requestableParts = false;
    return state;
  });
  builder.addCase(asyncQueries.getRequestableParts.fulfilled, (state, { payload: data }) => {
    state.loading.requestableParts = false;
    state.requestableParts = [...data.requestableParts];
    state.requestablePartsLastLoaded = new Date().toISOString();
    return state;
  });
  builder.addCase(asyncQueries.getSuggestedParts.pending, (state) => {
    state.loading.suggestedParts = true;
    return state;
  });
  builder.addCase(asyncQueries.getSuggestedParts.rejected, (state) => {
    state.loading.suggestedParts = false;
    return state;
  });
  builder.addCase(asyncQueries.getSuggestedParts.fulfilled, (state, { payload }) => {
    state.loading.suggestedParts = false;
    state.suggestedParts[payload.variables.machineId] = [...payload.data.suggestedParts];
    return state;
  });
  builder.addCase(asyncQueries.getFrequencyParts.pending, (state) => {
    state.loading.frequencyParts = true;
    return state;
  });
  builder.addCase(asyncQueries.getFrequencyParts.rejected, (state) => {
    state.loading.frequencyParts = false;
    return state;
  });
  builder.addCase(asyncQueries.getFrequencyParts.fulfilled, (state, { payload }) => {
    state.loading.frequencyParts = false;
    state.frequencyParts[payload.variables.modelGuid] = [...payload.data.frequencyParts];
    return state;
  });
};
